import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";

// ─── Types (mirror backend app/features/goals/schemas.py) ───────────────

export type GoalAccent =
  | "brand"
  | "power"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "neutral"
  | "purple"
  | "teal"
  | "orange";

export type ObjectiveStatus =
  | "not_started"
  | "in_progress"
  | "at_risk"
  | "on_hold"
  | "complete";

export interface Goal {
  id: string;
  year: number;
  key: string;
  title: string;
  short_label: string | null;
  tagline: string | null;
  description: string | null;
  success_criteria: string | null;
  accent: GoalAccent;
  icon: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface GoalSummary extends Goal {
  progress_pct: number;
  objective_count: number;
  status_counts: Record<string, number>;
}

export interface LinkedObjective {
  id: string;
  title: string;
  quarter_id: string | null;
  quarter_label: string | null;
  status: ObjectiveStatus;
  progress_pct: number;
  owner_id: string | null;
  owner_name: string | null;
  target_date: string | null;
}

export interface GoalDetail extends GoalSummary {
  objectives: LinkedObjective[];
}

export interface GoalYearOverview {
  year: number;
  headline: string;
  subhead: string | null;
  goals: GoalSummary[];
  /** Open WebUI ("Cinch AI") base URL for the deep-link; null when unset. */
  openwebui_url: string | null;
}

export interface GoalYear {
  year: number;
  headline: string;
  subhead: string | null;
}

export interface GoalWritePayload {
  key?: string;
  title?: string;
  short_label?: string | null;
  tagline?: string | null;
  description?: string | null;
  success_criteria?: string | null;
  accent?: GoalAccent;
  icon?: string | null;
  sort_order?: number;
  year?: number;
}

// ─── Query keys ──────────────────────────────────────────────────────────

const KEY = ["goals"] as const;

// ─── Queries ───────────────────────────────────────────────────────────────

export function useGoalsOverview(year?: number) {
  return useQuery({
    queryKey: [...KEY, "overview", year ?? "current"],
    queryFn: () =>
      apiFetch<GoalYearOverview>(`/goals/${year ? `?year=${year}` : ""}`),
    staleTime: 5 * 60 * 1000,
  });
}

export function useGoal(goalId: string | undefined) {
  return useQuery({
    queryKey: [...KEY, "detail", goalId],
    queryFn: () => apiFetch<GoalDetail>(`/goals/${goalId}`),
    enabled: !!goalId,
  });
}

export function useGoalYears() {
  return useQuery({
    queryKey: [...KEY, "years"],
    queryFn: () => apiFetch<GoalYear[]>("/goals/years"),
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────
// Fire-and-forget invalidation (don't await the refetch) — same rationale as
// objectives/api.ts: returning the promise keeps the mutation `isPending` until
// every refetch settles, hanging the dialog on the slow list refetch.

export function useCreateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: GoalWritePayload & { year: number; key: string; title: string }) =>
      apiFetch<Goal>("/goals/", { method: "POST", body: payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: GoalWritePayload }) =>
      apiFetch<Goal>(`/goals/${id}`, { method: "PATCH", body: payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/goals/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

// ─── AI insights (Phase 4) ─────────────────────────────────────────────────

export type AnalysisStatus = "pending" | "running" | "complete" | "failed";

export interface AgentQuery {
  sql: string;
  ok: boolean;
}

export interface GoalAnalysis {
  id: string;
  goal_id: string;
  status: AnalysisStatus;
  model: string | null;
  requested_by: string | null;
  direction: string | null;
  progress_assessment: string | null;
  clarified_success_criteria: string | null;
  recommendations: string[];
  queries: AgentQuery[];
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface OllamaModelInfo {
  name: string;
  size: number | null;
  is_default: boolean;
}

export interface OllamaModelsResponse {
  models: OllamaModelInfo[];
  configured: boolean;
  default_model: string | null;
}

/** Local models available for the analysis picker. */
export function useGoalModels() {
  return useQuery({
    queryKey: [...KEY, "models"],
    queryFn: () => apiFetch<OllamaModelsResponse>("/goals/ollama/models"),
    staleTime: 60_000,
  });
}

/** Latest analysis for a goal; null when none has been run. Auto-polls while a
 *  run is pending/running so the UI updates without a manual refresh. */
export function useLatestAnalysis(goalId: string | undefined) {
  return useQuery({
    queryKey: [...KEY, "analysis", goalId],
    queryFn: async () => {
      try {
        return await apiFetch<GoalAnalysis>(`/goals/${goalId}/analysis/latest`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null; // no run yet
        throw err;
      }
    },
    enabled: !!goalId,
    refetchInterval: (query) => {
      const status = (query.state.data as GoalAnalysis | null | undefined)?.status;
      return status === "running" || status === "pending" ? 2500 : false;
    },
  });
}

/** Kick off a background analysis. Seeds the latest-analysis cache so polling
 *  begins immediately. */
export function useStartAnalysis(goalId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { model: string | null; direction: string | null }) =>
      apiFetch<GoalAnalysis>(`/goals/${goalId}/analysis`, {
        method: "POST",
        body: { model: vars.model, direction: vars.direction },
      }),
    onSuccess: (data) => {
      // Seed the cache so the "running" state shows instantly…
      qc.setQueryData([...KEY, "analysis", goalId], data);
      // …then invalidate to force a real fetch. setQueryData alone doesn't
      // re-arm the latest-analysis query's refetchInterval, so without this the
      // poll never starts and the panel stays on "Analyzing…" until a reload.
      void qc.invalidateQueries({ queryKey: [...KEY, "analysis", goalId] });
    },
  });
}
